import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { ButtonMedium } from 'app/assets/styles'
import type { RootState } from 'shared/store/reducers'
import { colors as oldColors } from 'shared/assets/styles'
import { colors } from 'app-v2/design-systems/theme'
import { setReceiptUrl } from 'shared/store/ecommerce/order'
import { Loader } from 'app/common/loader'
import { useIntl } from 'react-intl'
import { TinyText } from 'app/common/typography'

const { yellow } = oldColors

type DownloadReceiptButtonProps = {
    orderId?: string,
    isDelivery: boolean
}

export const DownloadReceiptButton = ({
    orderId,
    isDelivery
}: DownloadReceiptButtonProps) => {
    const intl = useIntl()
    const dispatch = useDispatch()
    const target = isDelivery ? 'deliveryOrders' : 'cacOrders'
    const { receiptUrl } =
        useSelector((state: RootState) =>
            Object.values(state.ecommerceOrderState[target]).find(
                //$FlowFixMe
                order => order.id === orderId
            )
        ) || {}

    const [isLoading, setIsLoading] = useState(false)
    const [isOnSaveSucceed, setIsOnSaveSucceed] = useState(false)

    const downloadReceipt = useCallback(async () => {
        if (receiptUrl) {
            // Open link in a new tab
            window.open(receiptUrl, '_blank')
            setIsOnSaveSucceed(true)
        } else {
            // If no receipt URL is available, initiate action to get it
            setIsLoading(true)
            orderId && dispatch(setReceiptUrl(orderId, target))
        }
    }, [dispatch, orderId, receiptUrl, target])

    useEffect(() => {
        if (receiptUrl && isLoading) {
            setIsLoading(false)
            downloadReceipt()
        }
    }, [downloadReceipt, isLoading, isOnSaveSucceed, receiptUrl])

    return (
        <StyledContainer>
            <div>
                <StyledButtonMedium
                    onClick={downloadReceipt}
                    background="white"
                    color={yellow}
                    border={yellow}>
                    <StyledLoader loading={isLoading} color={yellow}>
                        {intl.formatMessage({
                            id: 'ecommerce.order.download.receipt'
                        })}
                    </StyledLoader>
                </StyledButtonMedium>
            </div>
            {isOnSaveSucceed && (
                <TinyText color={colors.crunchyGreen} center>
                    {intl.formatMessage({
                        id: 'ecommerce.order.download.succeed'
                    })}
                </TinyText>
            )}
        </StyledContainer>
    )
}

const StyledButtonMedium = styled(ButtonMedium)`
    margin: 14px 4px 7px 4px;
    width: 'auto';
    border: solid 1px ${yellow};
    border-radius: 10px;
`

const StyledContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin-bottom: 21px;
    > * {
        text-align: center;
    }
`

const StyledLoader = styled(Loader)`
    height: 20px;
`
